/**
 * PRODUCT CARD — swatch to add-to-cart sync
 *
 * On single option products the card adds straight to the side cart, so the hidden variant id in the card's
 * product form has to follow whichever swatch is selected. option-value.liquid already emits data-variant-id and
 * data-variant-available on each swatch input, so no extra payload is serialised per card.
 *
 * Delegated from the document rather than bound per card: collection sections are re-rendered wholesale by
 * facets-form on every filter change, and per-element listeners would be lost each time.
 *
 * Loaded site wide from layout/theme.liquid, because the card renders on collection, search, featured collection,
 * complementary products and shop the look.
 */
(() => {
  const SOLD_OUT_LABEL = "Sold out";

  /**
   * Replace the money text inside a price node without destroying its children.
   * price-list renders visually hidden labels ("Sale price", "Regular price") alongside the amount, so setting
   * textContent would silently strip them and degrade the card for screen readers.
   */
  function setMoney(node, value) {
    const textNodes = Array.from(node.childNodes).filter(
      (child) => child.nodeType === Node.TEXT_NODE && child.data.trim() !== "",
    );

    if (textNodes.length > 0) {
      textNodes[textNodes.length - 1].data = value;
    } else {
      node.textContent = value;
    }
  }

  function findPriceNodes(card) {
    const container = card.querySelector(".product-card__price");
    if (!container) return null;

    // price-list.liquid renders both elements in card context, with compare-at hidden until it applies, so there
    // is always a node to toggle. No DOM is built here.
    const compareAt = container.querySelector("compare-at-price");
    const sale = container.querySelector("sale-price");

    return sale ? { sale, compareAt } : null;
  }

  /**
   * Only worth touching the DOM when colours are actually priced differently, which is the exception rather than
   * the rule. Cached on the card so it is computed once, not on every swatch click.
   */
  function pricesVary(card) {
    if (card.dataset.pricesVary !== undefined)
      return card.dataset.pricesVary === "true";

    const prices = new Set(
      Array.from(card.querySelectorAll("input[data-variant-price]")).map(
        (input) => input.dataset.variantPrice,
      ),
    );

    const vary = prices.size > 1;
    card.dataset.pricesVary = String(vary);
    return vary;
  }

  function syncPrice(card, input) {
    if (!pricesVary(card)) return;

    const nodes = findPriceNodes(card);
    if (!nodes || !nodes.sale) return;

    const compareAt = input.dataset.variantCompareAtPrice;
    const onSale = Boolean(compareAt);

    setMoney(nodes.sale, input.dataset.variantPrice);

    /*
      The sale state is carried by classes on <sale-price>, not just by the presence of the compare-at node.
      Without toggling these, a card rendered on a discounted variant keeps showing the sale colour after the
      shopper picks a full price colour — which is what makes an undiscounted £34.99 render in orange.
    */
    nodes.sale.classList.toggle("text-on-sale", onSale);
    nodes.sale.classList.toggle("text-subdued", !onSale);

    if (nodes.compareAt) {
      nodes.compareAt.hidden = !onSale;
      if (onSale) setMoney(nodes.compareAt, compareAt);
    }
  }

  function syncCard(input) {
    const card = input.closest("product-card");
    if (!card) return;

    // Price follows the swatch on every card. The variant id sync below only applies to direct-add cards.
    syncPrice(card, input);

    const idField = card.querySelector("[data-card-variant-id]");
    if (!idField) return; // multi option card: the quick buy modal owns selection

    const button = card.querySelector(".product-card__add");
    const variantId = input.dataset.variantId;
    if (!variantId) return;

    idField.value = variantId;

    if (!button) return;

    // data-variant-available is the string "true" / "false" from Liquid.
    const available = input.dataset.variantAvailable !== "false";
    const label = button.querySelector("span");

    button.disabled = !available;
    button.classList.toggle("product-card__add--disabled", !available);

    if (label) {
      if (!label.dataset.defaultLabel)
        label.dataset.defaultLabel = label.textContent;
      label.textContent = available
        ? label.dataset.defaultLabel
        : SOLD_OUT_LABEL;
    }
  }

  document.addEventListener("change", (event) => {
    const input = event.target.closest?.("input[data-variant-id]");
    if (input) syncCard(input);
  });

  // A swatch can be pre-checked on render (or restored after a section re-render), so reconcile on load too.
  function syncAll(root = document) {
    root.querySelectorAll("input[data-variant-id]:checked").forEach(syncCard);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => syncAll());
  } else {
    syncAll();
  }

  // Sections re-rendered by faceting arrive after load; re-reconcile when the list changes.
  new MutationObserver((records) => {
    for (const record of records) {
      for (const node of record.addedNodes) {
        if (
          node.nodeType === 1 &&
          node.querySelector?.("input[data-variant-id]:checked")
        ) {
          syncAll(node);
        }
      }
    }
  }).observe(document.body, { childList: true, subtree: true });
})();
